import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/cn';
import { SPRING_PILL, useReducedMotion } from '../../lib/motion';

export interface SegmentOption<T extends string> {
  key: T;
  label: ReactNode;
}

/**
 * iOS-style segmented control with a single active "pill" that physically slides
 * between segments (shared-layout animation) rather than the background hard-
 * cutting from one segment to the next. Generic over the option key so both the
 * league hub tabs and the standings views can share one implementation.
 *
 * `layoutId` must be unique per on-screen instance so the pills don't try to
 * morph into each other.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  layoutId,
  trackClassName,
  itemClassName,
  pillClassName = 'bg-night',
  activeTextClassName = 'font-bold text-white',
  inactiveTextClassName = 'font-semibold text-[#6E6E73]'
}: {
  value: T;
  onChange: (key: T) => void;
  options: SegmentOption<T>[];
  layoutId: string;
  trackClassName?: string;
  itemClassName?: string;
  pillClassName?: string;
  activeTextClassName?: string;
  inactiveTextClassName?: string;
}) {
  const reduce = useReducedMotion();

  return (
    <div className={cn('relative flex gap-[5px] rounded-[14px] bg-fill p-[5px]', trackClassName)} role="tablist">
      {options.map((o) => {
        const on = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.key)}
            className={cn(
              'tap relative flex-1 rounded-[10px] py-[9px] text-center text-[13px] transition-colors',
              itemClassName,
              on ? activeTextClassName : inactiveTextClassName
            )}
          >
            {on && (
              <motion.span
                layoutId={layoutId}
                className={cn('absolute inset-0 rounded-[10px]', pillClassName)}
                transition={reduce ? { duration: 0 } : SPRING_PILL}
              />
            )}
            <span className="relative z-10">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
